import colors from 'theme/colors';

/**
 * Tokens compartilhados da visão de vendas (ingressos e produtos).
 *
 * A cor codifica **desempenho de venda** e nada mais. Uma escala só, aplicada
 * na seção "Desempenho por ingresso/produto": cinza = parado, âmbar = devagar,
 * roxo = vendendo, verde = forte.
 *
 * Regra que originou esta escala: a tela antiga pintava "99% disponível" de
 * verde — cor de sucesso comunicando fracasso de vendas. Aqui o tom sempre
 * acompanha o quanto foi vendido, nunca o quanto sobrou.
 *
 * Acessibilidade: `text` é sempre a variante escurecida, para uso sobre fundo
 * claro (≥4,5:1). Os tons de `fill` só preenchem barra, onde não há leitura.
 */

export const TONE = {
    NONE: 'none',
    SLOW: 'slow',
    SELLING: 'selling',
    STRONG: 'strong',
    SOLD_OUT: 'soldOut',
};

export const tone = {
    [TONE.NONE]: {
        text: '#5A5A5A',        // 7,0:1 sobre branco
        fill: '#C7C7C7',
        track: '#ECECEC',
        soft: '#F5F5F7',
        accent: '#C2C2C8',
        border: '#E2E2E6',
    },
    [TONE.SLOW]: {
        text: '#8A5300',        // 5,3:1 sobre branco
        fill: '#F0A22E',
        track: '#FBEBD3',
        soft: '#FFF8EC',
        accent: '#E8951A',
        border: '#F3DFAE',
    },
    [TONE.SELLING]: {
        text: '#6D14CC',        // 7,4:1 sobre branco
        fill: colors.secondaryPurple,
        track: '#EBDCFB',
        soft: '#F7EFFF',
        accent: colors.secondaryPurple,
        border: '#DFC8F8',
    },
    [TONE.STRONG]: {
        text: '#157F3C',        // 5,2:1 sobre branco
        fill: '#2BC866',
        track: '#D8F3E3',
        soft: '#F1FBF5',
        accent: '#1FA855',
        border: '#B7E5C9',
    },
    [TONE.SOLD_OUT]: {
        text: '#0F6B31',        // 6,3:1 sobre branco
        fill: '#157F3C',
        track: '#D8F3E3',
        soft: '#EDF9F2',
        accent: '#157F3C',
        border: '#A9DFC0',
    },
};

/** Traduz um percentual vendido (0–100) no tom semântico correspondente. */
export function toneForPercent(percent) {
    if (percent >= 100) return TONE.SOLD_OUT;
    if (percent >= 60) return TONE.STRONG;
    if (percent >= 20) return TONE.SELLING;
    if (percent > 0) return TONE.SLOW;
    return TONE.NONE;
}

/**
 * "Esgotando" é oportunidade, não problema — sinaliza a hora de abrir novo lote
 * ou ajustar preço. Fica separado da escala de desempenho justamente para não
 * disputar o âmbar com "vendendo devagar", que significa o oposto.
 */
export const SELLING_OUT_THRESHOLD = 90;

export function isSellingOut(percent) {
    return percent >= SELLING_OUT_THRESHOLD && percent < 100;
}

/**
 * Lote excluído pelo produtor. Declarado como função para não depender da ordem
 * de inicialização do módulo — os status abaixo já o usam.
 */
export function isBatchDeleted(batch) {
    return Boolean(batch?.is_deleted);
}

export const STATUS = {
    AVAILABLE: 'available',
    SOLD_OUT: 'soldOut',
    DISABLED: 'disabled',
    DELETED: 'deleted',
};

export const statusLabel = {
    [STATUS.AVAILABLE]: 'Disponível',
    [STATUS.SOLD_OUT]: 'Esgotado',
    [STATUS.DISABLED]: 'Desativado',
    [STATUS.DELETED]: 'Lote excluído',
};

export const statusChip = {
    [STATUS.AVAILABLE]: { bg: '#EDF9F2', text: '#0F6B31', border: '#B7E5C9' },
    [STATUS.SOLD_OUT]: { bg: '#F7EFFF', text: '#6D14CC', border: '#DFC8F8' },
    [STATUS.DISABLED]: { bg: '#F4F4F4', text: '#5A5A5A', border: '#DDDDDD' },
    // Excluído não é esgotado: o produtor tirou o lote de venda, ninguém comprou
    // até o fim. Cor própria para não parecer sucesso de vendas.
    [STATUS.DELETED]: { bg: '#FBF1F1', text: '#8A2B2B', border: '#EFD5D5' },
};

/**
 * Deriva o status de um item a partir dos seus lotes.
 *
 * Lote excluído sai da conta: ele chega com `quantity_available: 0` e continua
 * `is_active: 1`, então contá-lo faria um item ainda vendável parecer esgotado.
 */
export function statusForBatches(batches = []) {
    const sellable = batches.filter((b) => !isBatchDeleted(b));

    const hasAvailable = sellable.some((b) => b?.quantity_available > 0 && Number(b?.is_active) === 1);
    if (hasAvailable) return STATUS.AVAILABLE;

    const allSoldOut = sellable.length > 0 && sellable.every((b) => b?.quantity_available === 0);
    if (allSoldOut) return STATUS.SOLD_OUT;

    return STATUS.DISABLED;
}

/** Status de um lote isolado. */
export function statusForBatch(batch) {
    if (isBatchDeleted(batch)) return STATUS.DELETED;
    if (batch?.quantity_available === 0) return STATUS.SOLD_OUT;
    if (Number(batch?.is_active) === 0) return STATUS.DISABLED;
    return STATUS.AVAILABLE;
}

/**
 * A cadeia de valores, na ordem em que ela se compõe:
 *
 *     bruto − desconto = valor de face líquido de desconto
 *     pago  − taxa     = líquido do produtor
 *
 * O elo que faltava era o desconto: `value_sold_with_tax` e
 * `value_sold_without_tax` já chegavam com ele embutido, então o produtor via
 * quanto entrou sem ver quanto abateu. Nenhum dos dois novos elos é derivável
 * por subtração — `bruto − desconto` só coincide com o pago quando a taxa de
 * serviço é absorvida pelo produtor, e essa flag não vem neste payload.
 *
 * `knowsChain` existe porque produção pode estar atrás da API: sem os campos
 * novos a tela esconde a cadeia em vez de exibir zeros.
 */
function readMoney(source) {
    return {
        knowsChain: source?.value_gross !== undefined,
        gross: Number(source?.value_gross) || 0,
        discount: Number(source?.value_discount) || 0,
        charged: Number(source?.value_sold_with_tax) || 0,
        fees: Number(source?.value_tax) || 0,
        collected: Number(source?.value_sold_without_tax) || 0,
    };
}

/**
 * Números de um lote, já normalizados.
 *
 * A API expõe `quantity_sold` (pago) e `quantity_reserved` (pedido criado e não
 * pago). Antes só dava para inferir por `quantity - quantity_available`, o que
 * misturava os dois e errava em lote cujo estoque foi reduzido abaixo do vendido.
 * O caminho antigo fica como fallback porque produção pode estar atrás.
 */
function readBatch(batch) {
    const emitted = Number(batch?.quantity) || 0;
    const available = Number(batch?.quantity_available) || 0;
    const knowsBreakdown = batch?.quantity_sold !== undefined;

    const paid = knowsBreakdown ? Number(batch.quantity_sold) || 0 : Math.max(emitted - available, 0);
    const reserved = knowsBreakdown ? Number(batch.quantity_reserved) || 0 : 0;
    const deleted = Boolean(batch?.is_deleted);

    // Lote excluído mantém `quantity` como o emitido original e só zera o
    // disponível. Contar esse emitido inflaria o total com unidades que não
    // existem mais — e faria o card dizer "10 de 10 vendidos" onde saíram 3.
    return {
        ...readMoney(batch),
        paid,
        reserved,
        out: paid + reserved,
        deleted,
        total: deleted ? paid + reserved : emitted,
        available: deleted ? 0 : available,
        price: Number(batch?.price) || 0,
    };
}

/**
 * Agrega quantidades e valores dos lotes de um item.
 *
 * O dinheiro vem consolidado do próprio item quando a API o envia — é a mesma
 * soma, mas feita onde os valores nascem, sem acumular o arredondamento de
 * centavos de cada lote. As quantidades continuam sendo somadas aqui: o
 * consolidado da API não reproduz o tratamento de lote excluído do `readBatch`,
 * que conta só o que saiu em vez do estoque original.
 */
export function summarizeTicket(ticket) {
    const batches = ticket?.batches || [];

    const summary = batches.reduce(
        (acc, batch) => {
            const { paid, reserved, out, total, available, price, gross, discount, charged, fees, collected } =
                readBatch(batch);

            // `sold` segue significando "saiu do estoque", para a barra fechar
            // com `available`; `paid` e `reserved` separam pago de pendente.
            acc.sold += out;
            acc.paid += paid;
            acc.reserved += reserved;
            acc.total += total;
            acc.available += available;
            acc.estimated += total * price;

            acc.gross += gross;
            acc.discount += discount;
            acc.charged += charged;
            acc.fees += fees;
            acc.collected += collected;

            return acc;
        },
        {
            sold: 0, paid: 0, reserved: 0, total: 0, available: 0, estimated: 0,
            gross: 0, discount: 0, charged: 0, fees: 0, collected: 0,
        }
    );

    const consolidated = readMoney(ticket);

    if (!consolidated.knowsChain) {
        return { ...summary, knowsChain: false };
    }

    return { ...summary, ...consolidated };
}

/** Mesma leitura do resumo, para uma linha de lote isolada. */
export function summarizeBatch(batch) {
    return readBatch(batch);
}

export const percentOf = (part, whole) => (whole > 0 ? (part / whole) * 100 : 0);

/** Arredonda para inteiro sem transformar uma venda real em "0%". */
export const displayPercent = (value) => {
    if (value <= 0) return 0;
    if (value < 1) return 1;
    return Math.round(value);
};

export const formatInt = (value) => Number(value || 0).toLocaleString('pt-BR');
